import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";

const getBestThumbnail = (image) => {
  return (
    image.thumbnail_1000_url ||
    image.thumbnail_500_url ||
    image.thumbnail_url ||
    "https://via.placeholder.com/1500x1000?text=Thumbnail+Not+Available"
  );
};

const formatTime = (ms) => {
  const seconds = Math.ceil(ms / 1000);
  return `${seconds}s`;
};

const ImageSlider = memo(
  ({
    images,
    onSwiper,
    thumbnailSwiper,
    isPanelOpen,
    user,
    addToCollection,
    removeFromCollection,
    showCollection,
    collections,
    activeCollection,
    initialSlide = 0,
    addedImages,
    isAdmin,
    isSharedCollection,
  }) => {
    const [delay, setDelay] = useState(30000);
    const [isPlaying, setIsPlaying] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState(delay);
    const [selectedCollection, setSelectedCollection] = useState(
      collections[0] || ""
    );
    const swiperRef = useRef(null);
    const timerRef = useRef(null);

    const timerOptions = [
      { label: "30 sec", value: 30000 },
      { label: "1 min", value: 60000 },
      { label: "2 min", value: 120000 },
      { label: "5 min", value: 300000 },
    ];

    useEffect(() => {
      if (collections.length > 0 && !selectedCollection) {
        setSelectedCollection(collections[0]);
      }
    }, [collections, selectedCollection]);

    const resetTimer = useCallback(() => {
      setTimeRemaining(delay);
      if (
        isPlaying &&
        swiperRef.current?.swiper &&
        !swiperRef.current.swiper.destroyed
      ) {
        const swiper = swiperRef.current.swiper;
        if (!swiper.autoplay.running) {
          swiper.autoplay.start();
        }
      }
    }, [delay, isPlaying]);

    useEffect(() => {
      if (!swiperRef.current?.swiper) return;

      const swiper = swiperRef.current.swiper;
      onSwiper(swiper);

      const handleSlideChange = () => {
        if (thumbnailSwiper) {
          thumbnailSwiper.slideTo(swiper.activeIndex);
        }
        resetTimer();
      };

      swiper.on("slideChangeTransitionEnd", handleSlideChange);
      swiper.slideTo(initialSlide, 0);

      return () => {
        if (swiper && !swiper.destroyed) {
          swiper.off("slideChangeTransitionEnd", handleSlideChange);
        }
      };
    }, [onSwiper, thumbnailSwiper, resetTimer, initialSlide]);

    useEffect(() => {
      if (!swiperRef.current?.swiper) return;

      const swiper = swiperRef.current.swiper;

      if (isPlaying) {
        swiper.autoplay.start();
      } else {
        swiper.autoplay.stop();
      }

      return () => {
        if (swiper && !swiper.destroyed) {
          swiper.autoplay.stop();
        }
      };
    }, [isPlaying]);

    useEffect(() => {
      if (!swiperRef.current?.swiper) return;

      const swiper = swiperRef.current.swiper;
      swiper.params.autoplay.delay = delay;
      resetTimer();
    }, [delay, resetTimer]);

    useEffect(() => {
      resetTimer();
    }, [images, resetTimer]);

    useEffect(() => {
      if (!swiperRef.current?.swiper || !isPlaying) return;

      const swiper = swiperRef.current.swiper;

      if (isPanelOpen) {
        swiper.autoplay.pause();
      } else {
        swiper.autoplay.resume();
        resetTimer();
      }
    }, [isPanelOpen, isPlaying, resetTimer]);

    useEffect(() => {
      if (!isPlaying || isPanelOpen) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          const newTime = Math.max(prev - 1000, 0);
          return newTime;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [isPlaying, isPanelOpen]);

    useEffect(() => {
      if (!swiperRef.current?.swiper) return;

      const swiper = swiperRef.current.swiper;

      const updateTime = (swiperInstance, timeLeft) => {
        setTimeRemaining(Math.ceil(timeLeft));
      };

      swiper.on("autoplayTimeLeft", updateTime);

      return () => {
        if (swiper && !swiper.destroyed) {
          swiper.off("autoplayTimeLeft", updateTime);
        }
      };
    }, []);

    const handlePlayPause = () => {
      setIsPlaying((prev) => !prev);
    };

    const handleTimerChange = (newDelay) => {
      setDelay(newDelay);
      setTimeRemaining(newDelay);
    };

    const handleImageError = (e) => {
      e.target.src =
        "https://via.placeholder.com/1500x1000?text=Image+Load+Failed";
    };

    const memoizedSlides = useCallback(() => {
      return images.map((image) => {
        const isAdded =
          addedImages[image.id] && addedImages[image.id][selectedCollection];

        return (
          <SwiperSlide key={image.id}>
            <div className="relative h-full w-full flex items-center justify-center">
              <img
                src={getBestThumbnail(image)}
                alt={image.title || "Adobe Stock image"}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
                onError={handleImageError}
              />
              <div className="absolute hidden bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white text-sm truncate">
                  {image.title || "Untitled"}
                </p>
                <p className="text-white text-xs mt-1">ID: {image.id}</p>
              </div>
              {user && !showCollection && collections.length > 0 && (
                <div className="absolute bottom-0 right-0 flex gap-2 items-center justify-end w-full p-2 bg-[rgba(255,255,255,.3)] text-xs">
                  <span className="text-xs">Add to collection:</span>
                  <select
                    id={`collection-select-${image.id}`}
                    value={selectedCollection}
                    onChange={(e) => setSelectedCollection(e.target.value)}
                    className="px-2 py-1 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600"
                  >
                    {collections.map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => addToCollection(image, selectedCollection)}
                    className={`p-2 rounded-full transition-colors text-xs ${
                      isAdded
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                    aria-label={
                      isAdded ? "Added to collection" : "Add to collection"
                    }
                    disabled={isAdded}
                  >
                    {isAdded ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              )}
              {user && showCollection && (!isSharedCollection || isAdmin) && (
                <div className="absolute bottom-0 right-0 flex gap-2 items-center justify-end w-full p-2 bg-[rgba(255,255,255,.3)] text-xs">
                  <span className="text-xs">Remove from collection:</span>
                  <button
                    onClick={() => removeFromCollection(image.id)}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                    aria-label="Remove from collection"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </SwiperSlide>
        );
      });
    }, [
      images,
      selectedCollection,
      user,
      showCollection,
      collections,
      addToCollection,
      removeFromCollection,
      addedImages,
      isSharedCollection,
      isAdmin,
    ]);

    // Determine if the restricted-view class should be applied
    const shouldApplyClass = !user || (!isAdmin && isSharedCollection);

    return (
      <div className="w-4/5 max-w-2xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
          <div className="flex gap-2">
            {timerOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleTimerChange(option.value)}
                className={`px-3 py-1 rounded-md text-sm ${
                  delay === option.value
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePlayPause}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
            <div className="text-sm text-gray-500 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
              {formatTime(timeRemaining)}
            </div>
          </div>
        </div>

        <Swiper
          ref={swiperRef}
          cssMode={false}
          navigation={true}
          keyboard={true}
          autoplay={{
            delay: delay,
            disableOnInteraction: false,
            waitForTransition: true,
          }}
          modules={[Navigation, Keyboard, Autoplay]}
          className={`mySwiper rounded-lg overflow-hidden transition-colors duration-300 ${
            shouldApplyClass ? "restricted-view" : ""
          }`}
        >
          {memoizedSlides()}
        </Swiper>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isPanelOpen === nextProps.isPanelOpen &&
      prevProps.user === nextProps.user &&
      prevProps.showCollection === nextProps.showCollection &&
      prevProps.collections === nextProps.collections &&
      prevProps.activeCollection === nextProps.activeCollection &&
      prevProps.initialSlide === nextProps.initialSlide &&
      prevProps.images.length === nextProps.images.length &&
      prevProps.addedImages === nextProps.addedImages &&
      prevProps.isAdmin === nextProps.isAdmin &&
      prevProps.isSharedCollection === nextProps.isSharedCollection &&
      (prevProps.images.length === 0 ||
        prevProps.images[0]?.id === nextProps.images[0]?.id)
    );
  }
);

export default ImageSlider;
